<template>
  <div id="headerBarWrapper">
    <header id="headerBar">
      <div class="group">
        <PrimaryMenu @clearAllNotifications="$emit('clearAllNotifications')" />

        <!-- When the user interacts with the help popup from the profile menu, it will disappear on the focus-out
             event. This allows the help popup to remain up when the user profile menu disappears. -->
        <div v-if="isMobile" class="triggerHelp placeholder"></div>
      </div>

      <!-- TODO: Still need? In the new designs the whole header bar changes when in a workspace / app / whatever -->
      <div class="mobileWrapperGroup" v-if="isMobile && activeTrack">
        <button v-if="isMobile && showReturnToWorkspaceOverview" @click="returnToWorkspaceOverview" class="back">
          <BrandIcon name="special/chevron-left" title="back"></BrandIcon>
        </button>
        <button @click="handleMobileHeaderClick" class="title">
          <div class="mobileHeader" v-text="mobileHeader"></div>
          <div class="toggle" v-text="mobileHeaderToggleLabel"></div>
        </button>
      </div>

      <div class="group">
        <NonAppMenu @clearAllNotifications="$emit('clearAllNotifications')" />
      </div>
    </header>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef } from 'vue';

import BrandIcon from '@/components/icons/BrandIcon.vue';
import { useRoute, useRouter } from '@/composables/router/VueRouter';
import BrowserDetection from '@/data/BrowserDetection';
import { Track, TrackType } from '@/data/datatypes/Track';
import NonAppMenu from '@/NonAppMenu.vue';
import PrimaryMenu from '@/PrimaryMenu.vue';
import RouteNames from '@/router/RouteNames';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();
const uiStateStore = useUIStateStore();

const route = useRoute();
const router = useRouter();

interface Emits {
  (e: 'clearAllNotifications'): void,
}
defineEmits<Emits>();

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const showReturnToWorkspaceOverview: ComputedRef<boolean> = computed(() => {
  if (!isMobile.value) {
    return false;
  }
  if (route.name === RouteNames.TRACK_DASHBOARD_ROUTE_NAME) {
    return false;
  }
  if (!activeTrack.value) {
    return false;
  }
  // Check that we're in a child of the main track route
  let childRoute: boolean = false;
  for (const routeRecord of route.matched ?? []) {
    if (routeRecord.name === RouteNames.TRACK_MAIN_BASE_ROUTE_NAME) {
      childRoute = true;
    }
  }
  if (childRoute) {
    if (![TrackType.STANDARD, TrackType.BREAKOUT, TrackType.CHANNEL_CHAT].includes(activeTrack.value.type)) {
      childRoute = false;
    }
  }

  return childRoute;
});

function handleMobileHeaderClick(): void {
  const validTrackTypes = [TrackType.STANDARD, TrackType.BREAKOUT];
  if (activeTrack.value && validTrackTypes.includes(activeTrack.value.type)) {
    uiStateStore.setAdditionalContentVisible(!uiStateStore.showAdditionalContent);
  }
}

function returnToWorkspaceOverview(): void {
  if (trackEntriesStore.currentlyDisplayedFile) {
    trackEntriesStore.setCurrentlyDisplayedFile(null);
  }
  if (activeTrack.value) {
    router.push({
      name: RouteNames.TRACK_DASHBOARD_ROUTE_NAME,
      params: { trackId: activeTrack.value.id }
    });
  }
}

const activeTrack: ComputedRef<Track | undefined> = computed(() => {
  return tracksStore.activeTrack;
});

const mobileHeader: ComputedRef<string> = computed(() => {
  return activeTrack.value?.title ?? '';
});

const mobileHeaderToggleLabel: ComputedRef<string> = computed(() => {
  return `${uiStateStore.showAdditionalContent ? 'Hide' : 'Show'} details`;
});
</script>

<style scoped lang="scss">
$icon-line-height: 1.8rem;

#headerBar {
  background-color: $colour-background-light;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 4.25rem;
  height: 4.25rem;
  border-bottom: 1px solid $colour-background-mid;
  align-items: center;
  padding: 0 0.5rem;
  color: $colour-border-dark;
  max-width: 100%;

  .mobile & {
    height: 3.5rem;
    padding: 0;
    box-sizing: border-box;
    gap: 0.5rem;
    gap: 0;
  }

  .nativeIos & {
    padding-top: env(safe-area-inset-top);
    height: calc(2.5rem + env(safe-area-inset-top));
  }
}

.mobile {
  #headerBarWrapper {
    transition: 0.25s ease-in-out;
  }
}

.mobileWrapperGroup {
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
  height: 100%;
  max-width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;

  .brandIconContainer {
    margin-top: 0.125rem;
  }

  button {
    justify-content: flex-start;
    overflow: hidden;
    &.back {
      flex: 0 0 1.5rem;
    }
    &.title {
      flex: 1 1;
      margin-left: 0.5rem;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.25rem;
      border-radius: 0;
      overflow: hidden;
    }
  }

  .toggle {
    color: $colour-text-primary;
    font-size: $size-font-small;
  }
}
.mobileHeader {
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group {
  display: flex;
  flex: 1 1 33%;
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .mobile & {
    flex: 1 1 auto;

    &:first-child {
      flex: 0 0 auto;
    }
  }

  &:first-child {
    justify-content: flex-start;
    .mobile & {
      overflow: hidden;
    }
  }

  &:last-child {
    flex: 0 0 auto;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-right: 0.5rem;
  }
}

.icon {
  &.help {
    width: 2rem;
    height: 2rem;
  }
}

.triggerHelp.placeholder {
  // if we don't make it visible, then pendo will ignore it.
  width: 1px;
  height: 1px;
  pointer-events: none;
}
</style>
